import type {
  CoachThreadSummaryDto,
  ProcessRunSummaryDto,
  PersonaDto,
} from "@/client/types.gen"
import {
  TRAINING_ANLAESSE,
  istGesamtbild,
  type TrainingsEinheit,
} from "@nl/shared"
import { MEDIATHEK_ENABLED } from "@/lib/feature-flags"
import { productMedia } from "@/lib/product-media"

export type RecentEntry = {
  key: string
  kind: "chat" | "run"
  at: number
  title: string
  subtitle: string
  compact: string
  to: string
  search?: Record<string, string>
  threadId?: string
  /** Only set for chats; runs cannot be favorited. */
  favorite?: boolean
  image?: string
  progress?: string
}
/** Modes that live behind their own entry point and never reopen as a chat. */
export function hasOwnEntryPoint(mode: CoachThreadSummaryDto["mode"]) {
  return (
    mode === "preparation" || mode === "nachbereitung" || mode === "pruefung"
  )
}

export function threadDestination(
  thread: Pick<CoachThreadSummaryDto, "id" | "produkt"> &
    Partial<Pick<CoachThreadSummaryDto, "mode" | "lernfeld">>
) {
  // Modes with their own entry point reopen there, never in the general chat:
  // the page carries the framing the conversation was started under.
  if (thread.mode === "preparation")
    return {
      to: "/gespraechsauftakt",
      search: { gespraech: thread.id } as Record<string, string>,
    }
  if (thread.mode === "nachbereitung")
    return {
      to: "/nachbereitung",
      search: { gespraech: thread.id } as Record<string, string>,
    }
  // The focus area has to travel with the link: the API overwrites the stored
  // one with the request value on every turn, so a link without it would clear
  // the focus the advisor picked.
  if (thread.mode === "pruefung")
    return {
      to: "/pruefung",
      search: {
        gespraech: thread.id,
        ...(thread.lernfeld ? { lernfeld: thread.lernfeld } : {}),
      } as Record<string, string>,
    }
  const product = productMedia.find((product) => product.key === thread.produkt)
  return product
    ? {
        to: "/produkttraining",
        search: { produkt: product.key, gespraech: thread.id } as Record<
          string,
          string
        >,
      }
    : {
        to: MEDIATHEK_ENABLED ? "/chat" : "/",
        search: { gespraech: thread.id } as Record<string, string>,
      }
}

export function activityDate(iso: string, now: Date) {
  const date = new Date(iso)
  const day = (value: Date) =>
    new Intl.DateTimeFormat("en-CA", {
      timeZone: "Europe/Berlin",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(value)
  const dateDay = Date.parse(day(date))
  const today = Date.parse(day(now))
  const days = Math.round((today - dateDay) / 86400000)
  const time = new Intl.DateTimeFormat("de-DE", {
    timeZone: "Europe/Berlin",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date)
  if (days === 0) return `heute, ${time}`
  if (days === 1) return `gestern, ${time}`
  if (days > 1 && days < 7) return `vor ${days} Tagen`
  return new Intl.DateTimeFormat("de-DE", {
    timeZone: "Europe/Berlin",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date)
}

export function threadKindLabel(
  thread: Pick<CoachThreadSummaryDto, "produkt"> &
    Partial<Pick<CoachThreadSummaryDto, "mode">>
) {
  if (thread.mode === "preparation") return "Terminvorbereitung"
  if (thread.mode === "nachbereitung") return "Nachbereitung"
  if (thread.mode === "pruefung") return "Prüfungsvorbereitung"
  return thread.produkt ? "Produkttraining" : "Gespräch"
}

/** One titled thread as a card entry; shared by "Weitermachen" and "Favoriten". */
export function chatEntry(
  thread: CoachThreadSummaryDto,
  now: Date
): RecentEntry {
  return {
    key: `chat-${thread.id}`,
    kind: "chat",
    at: Date.parse(thread.updatedAt),
    title: thread.title!,
    compact: thread.title!,
    subtitle: `${threadKindLabel(thread)} · ${activityDate(thread.updatedAt, now)}`,
    ...threadDestination(thread),
    threadId: thread.id,
    favorite: thread.favorite,
    image: productMedia.find((p) => p.key === thread.produkt)?.image,
  }
}

/** The advisor's favorites, newest first, ready for the home section. */
export function favoriteEntries(
  threads: CoachThreadSummaryDto[],
  now: Date
): RecentEntry[] {
  return threads
    .filter((thread) => thread.favorite && thread.title)
    .map((thread) => chatEntry(thread, now))
    .sort((a, b) => b.at - a.at || a.key.localeCompare(b.key))
}

export function buildRecentEntries(
  threads: CoachThreadSummaryDto[],
  runs: ProcessRunSummaryDto[],
  personas: PersonaDto[],
  limit: number,
  now: Date
): RecentEntry[] {
  const personasById = new Map(personas.map((persona) => [persona.id, persona]))
  const chats: RecentEntry[] = threads
    .filter((thread) => thread.title)
    .map((thread) => chatEntry(thread, now))
  const trainings: RecentEntry[] = runs.map((run) => {
    const persona = personasById.get(run.personaId)
    const unitIndex = run.currentUnit ? run.units.indexOf(run.currentUnit) : -1
    const unit = unitIndex >= 0 ? unitIndex + 1 : run.units.length
    const running = run.status === "active"
    const progress = running
      ? `Einheit ${unit} von ${run.units.length}`
      : `${run.units.length} ${run.units.length === 1 ? "Einheit" : "Einheiten"}`
    const date = run.lastActivityAt ?? run.startedAt
    return {
      key: `run-${run.id}`,
      kind: "run",
      at: Date.parse(date),
      title: persona?.name ?? "Training",
      subtitle: `Training · ${activityDate(date, now)}`,
      compact: `${persona?.name ?? "Training"} · ${running ? `Einheit ${unit}/${run.units.length}` : progress}`,
      progress: running ? progress : undefined,
      image:
        TRAINING_ANLAESSE.find((topic) => topic.key === persona?.fall.anlass)
          ?.imageUrl ?? "/mediathek/filiale.jpg",
      // Ein abgeschlossener Durchlauf mit Nachbereitung öffnet dort wieder, wo
      // er geendet hat: die Schlussstation trägt das Gesamtbild, und die Leiste
      // mit allen Einheiten steht darüber. Nur wo es die Station nicht gibt,
      // bleibt der Rückblick der Einstieg — sonst landete man auf einer Seite
      // ohne Auswertung.
      to:
        running || run.units.some((u) => istGesamtbild(u as TrainingsEinheit))
          ? `/durchlauf/${run.id}`
          : `/durchlauf/${run.id}/review`,
    }
  })
  return [...chats, ...trainings]
    .sort((a, b) => b.at - a.at || a.key.localeCompare(b.key))
    .slice(0, limit)
}
